import { JsonFormErrorFloatingPane } from '../JsonFormErrorFloatingPane';
import { fakeRequired, uppercaseOptions, lightStyle } from '../ThinkalphaTheme';
import { rankWith, isOneOfEnumControl } from '@jsonforms/core';
import type { ControlProps, OwnPropsOfEnum } from '@jsonforms/core';
import { withJsonFormsControlProps } from '@jsonforms/react';
import { cn } from '@thinkalpha/ui-core/utils';
import type { KeysWithoutReturnObjects } from 'i18next';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Errors, Description, Label } from 'src/components/JsonForm/helpers';
import { useJsonFormExternalConfig } from 'src/components/JsonForm/JsonFormExternalConfigContext';
import { SelectValue, SelectTrigger, SelectItem, SelectContent, Select } from 'src/components/ui';
import { useEvent } from 'src/hooks/useEvent';
import {
    QUEUE_FOR_REMOVAL_FROM_ORDER_DATA_LABEL,
    QUEUE_FOR_REMOVAL_FROM_ORDER_DATA_VALUE,
} from 'src/lib/orders/removePropsFromOrderData';

export const OneOfEnumControlComponent = ({
    path,
    data,
    handleChange,
    description,
    errors,
    label,
    required,
    visible,
    schema,
}: ControlProps & OwnPropsOfEnum) => {
    const { t } = useTranslation();
    const config = useJsonFormExternalConfig();

    const hasErrors = errors.length > 0;

    const allowUnset = (schema as any)['x-allow-unset'] === true;

    const options = useMemo(() => {
        const baseOptions = schema.oneOf || [];

        // Add "— None —" option to UI if x-allow-unset is true
        return allowUnset
            ? [
                  { title: QUEUE_FOR_REMOVAL_FROM_ORDER_DATA_LABEL, const: QUEUE_FOR_REMOVAL_FROM_ORDER_DATA_VALUE },
                  ...baseOptions,
              ]
            : baseOptions;
    }, [schema.oneOf, allowUnset]);

    useEffect(() => {
        if (allowUnset) {
            return;
        }

        if (!allowUnset && options.length === 1 && data !== options[0].const) {
            setTimeout(() => handleChange(path, options[0].const));
        }

        // Only auto-select if not allowing unset and data is invalid
        if (options.length > 0 && !options.some((option) => option.const === data)) {
            handleChange(path, options[0].const);
        }
    }, [options, data, handleChange, path, allowUnset]);

    if (fakeRequired.includes(path)) {
        required = true; // make it look like required
    }

    const isLight = lightStyle.includes(path);

    const uppercase = uppercaseOptions.includes(path);

    const handleOnSelect = useEvent((value: unknown) => {
        handleChange(path, value);
    });

    // Map the actual data value to UI value for display
    const displayValue = useMemo(() => {
        if (allowUnset && (data === QUEUE_FOR_REMOVAL_FROM_ORDER_DATA_VALUE || data === undefined)) {
            return QUEUE_FOR_REMOVAL_FROM_ORDER_DATA_VALUE;
        }
        return data;
    }, [data, allowUnset]);

    return visible ? (
        <div>
            <div className="flex items-center gap-2">
                <Label label={label} required={required} />
                <Description description={description} />
            </div>

            <JsonFormErrorFloatingPane hasErrors={hasErrors} errors={<Errors errors={errors} />}>
                <Select onValueChange={handleOnSelect} value={displayValue}>
                    <SelectTrigger
                        variant={isLight ? 'light' : 'default'}
                        dimensions="sm"
                        className={cn(
                            uppercase && 'uppercase',
                            hasErrors &&
                                (isLight ? 'bg-[#fbcdc1] dark:border-[#7a0808]' : 'bg-[#1D110E] dark:border-[#4C1919]'),
                        )}
                    >
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {options.map((o: { title: string; const: string }) => {
                            const label = o.title ?? o.const;
                            const value = o.const ?? o.title;
                            return (
                                <SelectItem key={value} value={value} className={cn(uppercase && 'uppercase')}>
                                    {
                                        t(
                                            `${config.translationPrefix}::options::${label}` as KeysWithoutReturnObjects['json'],
                                            label,
                                        ) as unknown as string
                                    }
                                </SelectItem>
                            );
                        })}
                    </SelectContent>
                </Select>
            </JsonFormErrorFloatingPane>
        </div>
    ) : null;
};

export const OneOfEnumControlTester = rankWith(
    8, //increase rank as needed
    isOneOfEnumControl,
);

export const OneOfEnumControl = withJsonFormsControlProps(OneOfEnumControlComponent);
